import { Link, useMatch } from "react-router-dom";
import { useState, useRef, useEffect, useCallback } from "react";
import { firebase_auth } from "@src/utility_functions/database_operations/config/firebaseConfig";
import { Dropdown } from "@src/components/Dropdown/Dropdown";
import { HeaderButton } from "@src/components/Header/HeaderButton";
import { NewRowButtonsGrid } from "@src/components/Grids/ButtonGrids/NewRowButtonsGrid";
import { createTimeline } from "@src/utility_functions/database_operations/timelines/createTimeline";
import { ExportRowMenu } from "@src/components/Forms/Rows/ExportRow/ExportRowMenu";
import { CreateIntervalForm } from "@src/components/Forms/Intervals/CreateIntervalForm";
import { SaveCancelButtons } from "@src/components/Buttons/SaveCancelButtons";
import useTimelineData from "@src/utility_functions/customHooks/useTimelineData";
import { useModal } from "@src/utility_functions/customHooks/useModal";
import {
  CHART_TYPE_TIMELINE,
  CHART_TYPE_ROW,
} from "#shared/constants/chartTypes.js";

export const HeaderButtonsLoggedIn = (props) => {
  const { openDialog, closeDialog, setModalContent } = useModal();
  const matchTimeline = useMatch("/user/:userID/timeline/:timelineID");
  const matchRow = useMatch("/user/:userID/row/:rowID");
  const userID = matchTimeline?.params.userID || matchRow?.params.userID;
  const chartID = matchTimeline?.params.timelineID || matchRow?.params.rowID;

  const timelineDataContextObject = useTimelineData();
  const timelineDataFromContext = timelineDataContextObject?.timelineData ?? [];
  const timelineDataObject = timelineDataFromContext;

  const utilityButtonRef = useRef<HTMLButtonElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const [utilityDropdownOpen, setUtilityDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  const createNewTimelineClickHandler = async () => {
    const newTimelineObject = {
      chartType: "timeline",
    };
    const serverResponse = await createTimeline(newTimelineObject);
    if (serverResponse.error) {
      openDialog();
      setModalContent({
        modalTitle: serverResponse.errorDetails.title,
        errorModal: true,
        modalContent: (
          <div className="errorModalContainer">
            <p>{serverResponse.errorDetails.message}</p>
          </div>
        ),
      });
    } else if (serverResponse.payload.chartType === CHART_TYPE_TIMELINE) {
      window.location.href =
        "/user/" +
        props.loggedInUser?.id +
        "/timeline/" +
        serverResponse.payload.timelineID;
    } else {
      window.location.href =
        "/user/" +
        props.loggedInUser?.id +
        "/" +
        serverResponse.payload.chartType +
        "/" +
        serverResponse.payload.timelineID;
    }
  };

  const closeOpenMenus = useCallback(
    (event) => {
      if (
        !event.target.closest(".header_button_container") &&
        event.target !== utilityButtonRef.current &&
        event.target !== profileButtonRef.current
      ) {
        setUtilityDropdownOpen(false);
        setProfileDropdownOpen(false);
      }
    },
    [utilityDropdownOpen, profileDropdownOpen],
  );

  useEffect(() => {
    document.addEventListener("mousedown", closeOpenMenus);
  }, [closeOpenMenus]);

  const newIntervalDropdownObject = {
    text: "Create New Interval",
    clickHandler: () => {
      setModalContent({
        modalTitle: "Create Interval",
        modalContent: (
          <>
            <CreateIntervalForm
              customClass=""
              ownerID={userID}
              chartID={chartID}
              chartType={props.chartType}
              rowsIncludedState={props.rowsIncludedState}
              formID="createIntervalForm"
            />
          </>
        ),
        dialogFooterContent: (
          <SaveCancelButtons
            formID="createIntervalForm"
            cancelClickHandler={() => closeDialog()}
            isPending={false}
          />
        ),
      });
      openDialog();
    },
  };

  const newRowDropdownObject = {
    text: "Create New Row",
    clickHandler: () => {
      setModalContent({
        modalTitle: "Create New Row",
        modalContent: (
          <NewRowButtonsGrid
            loggedInUser={props.loggedInUser}
            chartType="row"
          />
        ),
      });
      openDialog();
    },
  };
  const newTimelineDropdownObject = {
    text: "Create New Timeline",
    clickHandler: createNewTimelineClickHandler,
  };
  const exportRowDropdownObject = {
    text: "Export Row",
    clickHandler: () => {
      setModalContent({
        modalTitle: "Create New Row",
        dialogWidth: "75%",
        modalContent: <ExportRowMenu timelineDataObject={timelineDataObject} />,
      });
      openDialog();
    },
  };
  const utilityDropdownItems = [
    newRowDropdownObject,
    newTimelineDropdownObject,
  ];
  if (
    props.chartType === CHART_TYPE_ROW ||
    props.chartType === CHART_TYPE_TIMELINE
  ) {
    utilityDropdownItems.unshift(newIntervalDropdownObject);
    utilityDropdownItems.push(exportRowDropdownObject);
  }

  return (
    <div>
      <div className="header_button_container _display--inline-block _margin-right--1rem">
        <Link to="/explore" className="header_button_text">
          <span>Explore</span>
        </Link>
      </div>
      <div className="header_button_container _display--inline-block _margin-right--1rem">
        <HeaderButton
          labelText="Actions Menu"
          buttonRef={utilityButtonRef}
          className="header_button_icon"
          icon="+"
          handleClick={() => {
            setProfileDropdownOpen(false);
            setUtilityDropdownOpen(!utilityDropdownOpen);
          }}
          dropdownOpen={utilityDropdownOpen}
        >
          <Dropdown items={utilityDropdownItems} buttonRef={utilityButtonRef} />
        </HeaderButton>
      </div>
      <div className="header_button_container _display--inline-block">
        <HeaderButton
          labelText="Profile Menu"
          buttonRef={profileButtonRef}
          className="header_button_text"
          icon={<i className="fa-solid fa-user"></i>}
          handleClick={() => {
            setUtilityDropdownOpen(false);
            setProfileDropdownOpen(!profileDropdownOpen);
          }}
          dropdownOpen={profileDropdownOpen}
        >
          <Dropdown
            items={[
              {
                reactRouterURL: "/user/" + props.loggedInUser?.id,
                text: props.loggedInUser?.username,
              },
              {
                text: "Logout",
                clickHandler: async () => {
                  await firebase_auth.signOut();
                  window.location.reload();
                },
              },
            ]}
            buttonRef={profileButtonRef}
          />
        </HeaderButton>
      </div>
    </div>
  );
};
